import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Palette, Settings, FileText, ClipboardList, Plus, Trash2,
  Save, RefreshCw, Mail, Upload, ImageIcon, GripVertical,
  ChevronDown, ChevronUp, Eye, Sparkles, Crown, X, Users,
  Check, ArrowRight, Type, List, CheckSquare, AlignLeft, Star,
  Scissors, Heart, ChefHat, Droplets
} from "lucide-react";

// Icon options for categories
const CATEGORY_ICONS = {
  sparkles: Sparkles, scissors: Scissors, heart: Heart, crown: Crown,
  palette: Palette, chef: ChefHat, star: Star, droplets: Droplets, users: Users
};

// Color presets
const COLOR_PRESETS = [
  { name: "Pink", value: "#ec4899" },
  { name: "Purple", value: "#8b5cf6" },
  { name: "Rose", value: "#f43f5e" },
  { name: "Violet", value: "#a855f7" },
  { name: "Orange", value: "#f97316" },
  { name: "Amber", value: "#f59e0b" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Blue", value: "#3b82f6" },
];

// Question type options
const QUESTION_TYPES = [
  { value: "text", label: "Short Text", icon: Type },
  { value: "textarea", label: "Long Text", icon: AlignLeft },
  { value: "multiselect", label: "Multi-select", icon: CheckSquare },
  { value: "yesno", label: "Yes/No", icon: Check },
  { value: "toggle", label: "Toggle", icon: List },
  { value: "file", label: "File Upload", icon: Upload },
];

export default function PresetConfiguratorModal({ 
  isOpen, 
  onClose, 
  category = null, 
  onSave,
  isEdit = false 
}) {
  const [activeTab, setActiveTab] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    icon: "sparkles",
    color: "#ec4899",
    is_active: true,
    icon_url: "",
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const handleIconUpload = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "category_icons");
      const res = await fetch(`${API}/pilot/upload-image`, { method: "POST", body: fd });
      if (res.ok) {
        const data = await res.json();
        const url = data.url || data.secure_url;
        setFormData(prev => ({ ...prev, icon_url: url }));
      }
    } catch {} finally { setUploadingIcon(false); }
  };
  
  // Branding state
  const [branding, setBranding] = useState({
    primary_color: "#ec4899",
    secondary_color: "#1a1a2e",
    logo_url: "",
    cover_image: "",
    envelope_text: "YOU'RE INVITED",
    envelope_bg_color: "#1a1a2e",
    envelope_text_color: "#ffffff",
    welcome_title: "",
    welcome_subtitle: "",
  });
  
  // Intake sections state
  const [sections, setSections] = useState([]);
  
  // Load existing data when editing
  useEffect(() => {
    if (isOpen && category && isEdit) {
      setFormData({
        name: category.name || "",
        slug: category.slug || "",
        description: category.description || "",
        icon: category.icon || "sparkles",
        color: category.color || "#ec4899",
        is_active: category.is_active !== false,
        icon_url: category.icon_url || "",
        preference_schema_json: category.preference_schema ? JSON.stringify(category.preference_schema, null, 2) : "",
        default_inspo: category.default_inspo || [],
        default_services: category.default_services || [],
      });
      
      // Load branding from category
      if (category.branding) {
        setBranding(prev => ({ ...prev, ...category.branding }));
      }
      
      // Fetch intake template
      fetchIntakeTemplate(category.slug || category.name?.toLowerCase());
    } else if (isOpen && !isEdit) {
      // Reset form for new category
      setFormData({
        name: "",
        slug: "",
        description: "",
        icon: "sparkles",
        color: "#ec4899",
        is_active: true,
        icon_url: "",
      });
      setBranding({
        primary_color: "#ec4899",
        secondary_color: "#1a1a2e",
        logo_url: "",
        cover_image: "",
        envelope_text: "YOU'RE INVITED",
        envelope_bg_color: "#1a1a2e",
        envelope_text_color: "#ffffff",
        welcome_title: "",
        welcome_subtitle: "",
      });
      setSections([]);
    }
  }, [isOpen, category, isEdit]);
  
  // Fetch intake template from backend
  const fetchIntakeTemplate = async (cat) => {
    setLoadingTemplate(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/intake-template/${cat}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sections && data.sections.length > 0) {
          setSections(data.sections);
        }
        if (data.branding) {
          setBranding(prev => ({ ...prev, ...data.branding }));
        }
      }
    } catch (err) {
      console.error("Error fetching template:", err);
    } finally {
      setLoadingTemplate(false);
    }
  };
  
  // Generate slug from name
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };
  
  // Handle form changes
  const updateForm = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "name") {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };
  
  const updateBranding = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };
  
  // Section management
  const addSection = () => {
    setSections(prev => [...prev, {
      id: `section_${Date.now()}`,
      title: "New Section",
      subtitle: "",
      icon: "Settings",
      questions: []
    }]);
  };
  
  const updateSection = (sectionIndex, field, value) => {
    setSections(prev => prev.map((s, i) => 
      i === sectionIndex ? { ...s, [field]: value } : s
    ));
  };
  
  const deleteSection = (sectionIndex) => {
    setSections(prev => prev.filter((_, i) => i !== sectionIndex));
  };
  
  // Question management
  const addQuestion = (sectionIndex) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return {
        ...s,
        questions: [...s.questions, {
          id: `q_${Date.now()}`,
          type: "text",
          label: "New Question",
          required: false,
          placeholder: "",
          options: []
        }]
      };
    }));
  };
  
  const updateQuestion = (sectionIndex, questionIndex, field, value) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return {
        ...s,
        questions: s.questions.map((q, j) => 
          j === questionIndex ? { ...q, [field]: value } : q
        )
      };
    }));
  };
  
  const deleteQuestion = (sectionIndex, questionIndex) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      return {
        ...s,
        questions: s.questions.filter((_, j) => j !== questionIndex)
      };
    }));
  };
  
  // Save all data
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      setActiveTab("basics");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Save category with branding. Parse preference_schema from JSON textarea.
      let preference_schema = null;
      if (formData.preference_schema_json && formData.preference_schema_json.trim()) {
        try { preference_schema = JSON.parse(formData.preference_schema_json); } catch {}
      }
      const payload = {
        ...formData,
        branding,
        sections, // Include sections for template
        preference_schema: preference_schema || undefined,
      };
      delete payload.preference_schema_json;
      
      // Call the parent onSave handler
      if (onSave) {
        await onSave(payload);
      }
      
      // Also update the pilot_templates collection - use preset_id not slug
      const templateCategory = formData.preset_id || formData.slug || generateSlug(formData.name);
      await fetch(`${API}/admin/pilot-templates/${templateCategory}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category: templateCategory,
          sections,
          branding,
          updated_at: new Date().toISOString()
        })
      });
      
      toast.success(isEdit ? "Category updated!" : "Category created!");
      onClose();
    } catch (err) {
      toast.error("Failed to save category");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Settings className="w-5 h-5" />
            {isEdit ? `Edit ${formData.name || "Category"}` : "Create New Pilot Category"}
          </DialogTitle>
          <DialogDescription>
            Configure all settings, branding, and intake questions for this category
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="w-full justify-start px-6 pt-2 border-b bg-gray-50 rounded-none h-auto flex-wrap gap-1">
            <TabsTrigger value="basics" className="data-[state=active]:bg-white rounded-b-none">
              <Settings className="w-4 h-4 mr-2" /> Basics
            </TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-white rounded-b-none">
              <Palette className="w-4 h-4 mr-2" /> Branding
            </TabsTrigger>
            <TabsTrigger value="intake" className="data-[state=active]:bg-white rounded-b-none">
              <ClipboardList className="w-4 h-4 mr-2" /> Intake Questions
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-white rounded-b-none">
              <Eye className="w-4 h-4 mr-2" /> Preview
            </TabsTrigger>
          </TabsList>
          
          <ScrollArea className="flex-1">
            {/* BASICS TAB */}
            <TabsContent value="basics" className="p-6 space-y-6 m-0">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Category Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => updateForm("name", e.target.value)}
                      placeholder="e.g., Makeup Artists"
                      data-testid="preset-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>URL Slug</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => updateForm("slug", e.target.value)}
                      placeholder="makeup-artists"
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Used in URLs: /pilots/{formData.slug || "slug"}
                    </p>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => updateForm("description", e.target.value)}
                      placeholder="Brief description..."
                      rows={3}
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>Icon</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(CATEGORY_ICONS).map(([key, Icon]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => updateForm("icon", key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.icon === key 
                              ? "border-current scale-105" 
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          style={{ borderColor: formData.icon === key ? formData.color : undefined }}
                        >
                          <Icon className="w-5 h-5" style={{ color: formData.color }} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-1">
                      <ImageIcon className="w-3.5 h-3.5" /> Custom Icon Image
                      <span className="text-[10px] text-gray-400 ml-1">overrides Lucide icon system-wide</span>
                    </Label>
                    <div className="mt-2 flex items-center gap-3">
                      {formData.icon_url ? (
                        <div className="relative w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center" data-testid="preset-icon-preview">
                          <img src={formData.icon_url} alt="Custom icon" className="max-w-full max-h-full object-contain" />
                          <button
                            type="button"
                            onClick={() => updateForm("icon_url", "")}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                            title="Remove custom icon"
                            data-testid="preset-icon-remove"
                          >×</button>
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                          <ImageIcon className="w-6 h-6" />
                        </div>
                      )}
                      <label className="flex-1 cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/svg+xml,image/jpeg,image/webp"
                          className="hidden"
                          onChange={(e) => handleIconUpload(e.target.files?.[0])}
                          data-testid="preset-icon-upload"
                        />
                        <div className="px-3 py-2.5 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-sm text-gray-600 text-center transition-colors">
                          {uploadingIcon ? "Uploading..." : (formData.icon_url ? "Replace icon" : "Upload to Cloudinary")}
                        </div>
                      </label>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">PNG/SVG recommended. Hosted on Cloudinary; updates everywhere this category appears.</p>
                  </div>
                  
                  <div>
                    <Label>Brand Color</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {COLOR_PRESETS.map(({ name, value }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            updateForm("color", value);
                            updateBranding("primary_color", value);
                          }}
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            formData.color === value 
                              ? "border-gray-800 scale-110 ring-2 ring-offset-2" 
                              : "border-transparent hover:scale-105"
                          }`}
                          style={{ backgroundColor: value }}
                          title={name}
                        />
                      ))}
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => {
                          updateForm("color", e.target.value);
                          updateBranding("primary_color", e.target.value);
                        }}
                        className="w-10 h-10 rounded-full cursor-pointer border-2 border-dashed border-gray-300"
                        title="Custom color"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-4">
                    <div>
                      <Label>Active</Label>
                      <p className="text-xs text-gray-500">Show this category to users</p>
                    </div>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => updateForm("is_active", checked)}
                    />
                  </div>

                  {/* ── /me Plan-an-Experience controls (sync fields) ── */}
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div>
                      <Label className="text-xs">Preference Schema (JSON array of preset options for /me Plan-an-Experience)</Label>
                      <textarea
                        value={formData.preference_schema_json || ""}
                        onChange={(e) => updateForm("preference_schema_json", e.target.value)}
                        rows={3}
                        placeholder='[{"key":"length","label":"Length","options":["Short","Medium","Long"]}]'
                        className="w-full text-[11px] font-mono border border-gray-200 rounded-md p-2 mt-1"
                        data-testid="preset-pref-schema"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Hair: avoid "Face" fields. JSON array of {"{key, label, options}"} objects.</p>
                    </div>
                    <div>
                      <Label className="text-xs">Default Inspo Folder (newline-separated URLs, used for empty user libraries)</Label>
                      <textarea
                        value={(formData.default_inspo || []).join("\n")}
                        onChange={(e) => updateForm("default_inspo", e.target.value.split("\n").filter(Boolean))}
                        rows={3}
                        placeholder="https://res.cloudinary.com/dkh2svcxu/image/upload/..."
                        className="w-full text-[11px] font-mono border border-gray-200 rounded-md p-2 mt-1"
                        data-testid="preset-default-inspo"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Default Services (newline-separated names, auto-suggested in Plan-an-Experience)</Label>
                      <textarea
                        value={(formData.default_services || []).join("\n")}
                        onChange={(e) => updateForm("default_services", e.target.value.split("\n").filter(Boolean))}
                        rows={3}
                        placeholder="Soft Glam Makeup&#10;Bridal Updo"
                        className="w-full text-[11px] font-mono border border-gray-200 rounded-md p-2 mt-1"
                        data-testid="preset-default-services"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <Card className="border-dashed">
                <CardContent className="pt-4">
                  <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
                  <div 
                    className="inline-flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ backgroundColor: `${formData.color}15` }}
                  >
                    {(() => {
                      const Icon = CATEGORY_ICONS[formData.icon] || CATEGORY_ICONS.sparkles;
                      return <Icon className="w-6 h-6" style={{ color: formData.color }} />;
                    })()}
                    <div>
                      <p className="font-semibold" style={{ color: formData.color }}>
                        {formData.name || "Category Name"}
                      </p>
                      <p className="text-xs text-gray-500">{formData.description || "Description"}</p>
                    </div>
                    {formData.is_active && <Badge className="bg-green-500 text-xs">Active</Badge>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* BRANDING TAB */}
            <TabsContent value="branding" className="p-6 space-y-6 m-0">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Envelope Settings
                  </h3>
                  
                  <div>
                    <Label>Envelope Text</Label>
                    <Input
                      value={branding.envelope_text}
                      onChange={(e) => updateBranding("envelope_text", e.target.value)}
                      placeholder="YOU'RE INVITED"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Background</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={branding.envelope_bg_color}
                          onChange={(e) => updateBranding("envelope_bg_color", e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={branding.envelope_bg_color}
                          onChange={(e) => updateBranding("envelope_bg_color", e.target.value)}
                          className="font-mono text-sm flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Text Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={branding.envelope_text_color}
                          onChange={(e) => updateBranding("envelope_text_color", e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer"
                        />
                        <Input
                          value={branding.envelope_text_color}
                          onChange={(e) => updateBranding("envelope_text_color", e.target.value)}
                          className="font-mono text-sm flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Welcome Page
                  </h3>
                  
                  <div>
                    <Label>Welcome Title</Label>
                    <Input
                      value={branding.welcome_title}
                      onChange={(e) => updateBranding("welcome_title", e.target.value)}
                      placeholder="Welcome to your portal!"
                    />
                  </div>
                  
                  <div>
                    <Label>Welcome Subtitle</Label>
                    <Textarea
                      value={branding.welcome_subtitle}
                      onChange={(e) => updateBranding("welcome_subtitle", e.target.value)}
                      placeholder="We're excited to have you..."
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label>Logo URL</Label>
                    <Input
                      value={branding.logo_url}
                      onChange={(e) => updateBranding("logo_url", e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
              
              {/* Envelope Preview */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Envelope Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="h-40 rounded-xl flex flex-col items-center justify-center text-center"
                    style={{ backgroundColor: branding.envelope_bg_color }}
                  >
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center mb-4 border-2"
                      style={{ 
                        backgroundColor: formData.color, 
                        borderColor: branding.envelope_text_color 
                      }}
                    >
                      {(() => {
                        const Icon = CATEGORY_ICONS[formData.icon] || CATEGORY_ICONS.sparkles;
                        return <Icon className="w-7 h-7 text-white" />;
                      })()}
                    </div>
                    <p 
                      className="text-xl font-bold tracking-widest uppercase"
                      style={{ color: branding.envelope_text_color }}
                    >
                      {branding.envelope_text || "YOU'RE INVITED"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* INTAKE QUESTIONS TAB */}
            <TabsContent value="intake" className="p-6 space-y-4 m-0">
              {loadingTemplate ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">Loading intake template...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">Intake Form Sections</h3>
                      <p className="text-sm text-gray-500">Define what information to collect from providers</p>
                    </div>
                    <Button onClick={addSection} size="sm">
                      <Plus className="w-4 h-4 mr-2" /> Add Section
                    </Button>
                  </div>
                  
                  {sections.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center">
                        <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-gray-500">No sections yet</p>
                        <Button onClick={addSection} variant="outline" className="mt-3">
                          <Plus className="w-4 h-4 mr-2" /> Add Your First Section
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {sections.map((section, sectionIndex) => (
                        <Card key={section.id} className="overflow-hidden">
                          <CardHeader className="py-3 px-4 bg-gray-50 border-b">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
                                <Input
                                  value={section.title}
                                  onChange={(e) => updateSection(sectionIndex, "title", e.target.value)}
                                  className="h-8 font-semibold max-w-xs"
                                  placeholder="Section Title"
                                />
                                <Input
                                  value={section.subtitle || ""}
                                  onChange={(e) => updateSection(sectionIndex, "subtitle", e.target.value)}
                                  className="h-8 text-sm text-gray-500 max-w-sm"
                                  placeholder="Subtitle (optional)"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant="outline">{section.questions.length} questions</Badge>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => deleteSection(sectionIndex)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="p-4 space-y-3">
                            {section.questions.map((question, questionIndex) => (
                              <div key={question.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                <GripVertical className="w-4 h-4 text-gray-300 mt-2 cursor-move" />
                                <div className="flex-1 grid grid-cols-3 gap-2">
                                  <Input
                                    value={question.label}
                                    onChange={(e) => updateQuestion(sectionIndex, questionIndex, "label", e.target.value)}
                                    placeholder="Question label"
                                    className="col-span-2 h-9"
                                  />
                                  <select
                                    value={question.type}
                                    onChange={(e) => updateQuestion(sectionIndex, questionIndex, "type", e.target.value)}
                                    className="h-9 rounded-md border border-gray-200 text-sm px-2"
                                  >
                                    {QUESTION_TYPES.map(t => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                  {question.type === "multiselect" && (
                                    <Input
                                      value={(question.options || []).join(", ")}
                                      onChange={(e) => updateQuestion(sectionIndex, questionIndex, "options", e.target.value.split(",").map(o => o.trim()))}
                                      placeholder="Options (comma-separated)"
                                      className="col-span-2 h-9 text-sm"
                                    />
                                  )}
                                  <Input
                                    value={question.placeholder || ""}
                                    onChange={(e) => updateQuestion(sectionIndex, questionIndex, "placeholder", e.target.value)}
                                    placeholder="Placeholder text"
                                    className="col-span-2 h-9 text-sm"
                                  />
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center gap-1 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={question.required}
                                        onChange={(e) => updateQuestion(sectionIndex, questionIndex, "required", e.target.checked)}
                                        className="rounded"
                                      />
                                      Required
                                    </label>
                                    <label className="flex items-center gap-1 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={question.has_other}
                                        onChange={(e) => updateQuestion(sectionIndex, questionIndex, "has_other", e.target.checked)}
                                        className="rounded"
                                      />
                                      Has "Other"
                                    </label>
                                  </div>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => deleteQuestion(sectionIndex, questionIndex)}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => addQuestion(sectionIndex)}
                              className="w-full border-dashed"
                            >
                              <Plus className="w-4 h-4 mr-2" /> Add Question
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            
            {/* PREVIEW TAB */}
            <TabsContent value="preview" className="p-6 m-0">
              <div className="grid grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Envelope
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="h-48 rounded-xl flex flex-col items-center justify-center"
                      style={{ backgroundColor: branding.envelope_bg_color }}
                    >
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: formData.color }}
                      >
                        {(() => {
                          const Icon = CATEGORY_ICONS[formData.icon] || CATEGORY_ICONS.sparkles;
                          return <Icon className="w-8 h-8 text-white" />;
                        })()}
                      </div>
                      <p 
                        className="text-lg font-bold tracking-widest"
                        style={{ color: branding.envelope_text_color }}
                      >
                        {branding.envelope_text}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" /> Intake Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sections.map(section => (
                        <div key={section.id} className="flex items-center justify-between py-2 border-b last:border-0">
                          <span className="font-medium">{section.title}</span>
                          <Badge variant="outline">{section.questions.length} questions</Badge>
                        </div>
                      ))}
                      {sections.length === 0 && (
                        <p className="text-gray-400 text-center py-4">No intake sections configured</p>
                      )}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-500">
                        Total: <strong>{sections.reduce((acc, s) => acc + s.questions.length, 0)}</strong> questions across <strong>{sections.length}</strong> sections
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
        
        <DialogFooter className="px-6 py-4 border-t bg-gray-50 shrink-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-preset-btn">
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />{isEdit ? "Save Changes" : "Create Category"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
